// Battle Manager - 战斗管理器
// 处理妖兽战斗逻辑，回合制战斗系统

import { beastSkills } from './beast-data.js';
import { STATUS_EFFECTS } from '../resources.js';

class BattleManager {
  constructor(stateManager) {
    this.stateManager = stateManager;
    this.currentBattle = null;
  }

  // 开始战斗
  startBattle(player, beast) {
    this.currentBattle = {
      player: {
        ...player,
        currentQi: player.qi,
        maxQi: player.maxQi || player.qi, // 确保有正确的最大血量
        effects: [] // 状态效果
      },
      beast: {
        ...beast,
        effects: [] // 状态效果
      },
      turn: 0, // 回合数
      log: [], // 战斗日志
      isPlayerTurn: true, // 是否玩家回合
      battleResult: null // 战斗结果
    };

    // 根据速度决定先手
    if (beast.stats.speed > player.speed) {
      this.currentBattle.isPlayerTurn = false;
      // 妖兽先手，立即执行妖兽攻击
      setTimeout(() => {
        this.beastAttack();
      }, 1000);
    }

    this.addBattleLog(`遭遇了${beast.name}！战斗开始！`);
    this.addBattleLog(`玩家血量: ${this.currentBattle.player.currentQi}/${player.maxQi || player.qi}`);
    this.addBattleLog(`妖兽血量: ${this.currentBattle.beast.currentQi}/${beast.stats.qi}`);

    return this.getBattleState();
  }

  // 执行玩家攻击
  playerAttack(skillName) {
    if (!this.currentBattle || !this.currentBattle.isPlayerTurn) {
      return { success: false, message: '不是你的回合' };
    }

    const player = this.currentBattle.player;
    const beast = this.currentBattle.beast;

    // 检查是否有可用技能
    if (!player.skills || !Array.isArray(player.skills) || !player.skills.includes(skillName)) {
      return { success: false, message: '你不会这个技能' };
    }

    // 简化：使用基础攻击，至少造成1点伤害
    const damage = Math.max(1, Math.floor((player.attack * 1.2) - beast.stats.defense));
    beast.currentQi -= damage;

    this.addBattleLog(`你使用了${skillName}，造成${damage}点伤害！`);
    this.addBattleLog(`妖兽血量: ${beast.currentQi}/${beast.stats.qi}`);

    // 检查战斗是否结束
    if (beast.currentQi <= 0) {
      this.endBattle('victory');
      return { success: true, battleEnd: true, result: 'victory' };
    }

    // 切换到妖兽回合
    this.currentBattle.isPlayerTurn = false;
    this.currentBattle.turn++;

    // 妖兽自动攻击
    setTimeout(() => {
      this.beastAttack();
    }, 1000);

    return { success: true, damage: damage };
  }

  // 妖兽攻击
  beastAttack() {
    if (!this.currentBattle) return;

    const player = this.currentBattle.player;
    const beast = this.currentBattle.beast;

    // 妖兽随机选择技能
    const availableSkills = beast.skills || ['撕咬'];
    const skillName = availableSkills[Math.floor(Math.random() * availableSkills.length)];
    const skill = beastSkills[skillName] || { damage: 1.0, type: 'physical' };

    // 计算伤害
    let damage = Math.floor(beast.stats.attack * skill.damage);
    damage = Math.max(1, damage - player.defense);

    // 特殊效果处理
    if (skill.effect) {
      if (skill.effect.poison) {
        player.effects.push({ type: 'poison', duration: skill.effect.poison, damage: 3 });
        this.addBattleLog(`${beast.name}的${skillName}带有毒性，你中毒了！`);
      }
      if (skill.effect.stun) {
        player.effects.push({ type: 'stun', duration: skill.effect.stun });
        this.addBattleLog(`${beast.name}的${skillName}让你眩晕了！`);
      }
    }

    player.currentQi -= damage;
    this.addBattleLog(`${beast.name}使用了${skillName}，造成${damage}点伤害！`);
    this.addBattleLog(`玩家血量: ${player.currentQi}/${player.maxQi || player.qi}`);

    // 处理状态效果
    this.processEffects(player);

    // 检查战斗是否结束
    if (player.currentQi <= 0) {
      this.endBattle('defeat');
      return;
    }

    // 切换回玩家回合
    this.currentBattle.isPlayerTurn = true;
  }

  // 处理状态效果
  processEffects(target) {
    target.effects = target.effects.filter(effect => {
      effect.duration--;

      if (effect.type === 'poison' && effect.duration > 0) {
        const poisonDamage = effect.damage;
        target.currentQi -= poisonDamage;
        this.addBattleLog(`${target === this.currentBattle.player ? '你' : target.name}受到${poisonDamage}点毒性伤害！`);
      }

      return effect.duration > 0;
    });
  }

  // 玩家逃跑
  playerFlee() {
    if (!this.currentBattle || !this.currentBattle.isPlayerTurn) {
      return { success: false, message: '不是你的回合' };
    }

    // 逃跑成功率基于速度差异
    const playerSpeed = this.currentBattle.player.speed;
    const beastSpeed = this.currentBattle.beast.stats.speed;
    const fleeChance = Math.min(0.8, Math.max(0.2, (playerSpeed - beastSpeed) / 100 + 0.5));

    if (Math.random() < fleeChance) {
      this.endBattle('fled');
      return { success: true, result: 'fled' };
    } else {
      this.addBattleLog('逃跑失败！');
      this.currentBattle.isPlayerTurn = false;
      this.currentBattle.turn++;

      // 妖兽反击
      setTimeout(() => {
        this.beastAttack();
      }, 1000);

      return { success: false, message: '逃跑失败' };
    }
  }

  // 结束战斗
  endBattle(result) {
    this.currentBattle.battleResult = result;

    switch (result) {
      case 'victory':
        this.addBattleLog('战斗胜利！你击败了妖兽！');
        // 处理战利品
        this.handleVictoryRewards();
        // 检查是否受伤严重
        this.checkInjuryStatus();
        break;
      case 'defeat':
        this.addBattleLog('战斗失败！你被妖兽击败了！');
        // 处理失败惩罚
        this.handleDefeatPenalties();
        break;
      case 'fled':
        this.addBattleLog('你成功逃脱了战斗！');
        break;
    }

    // 清理战斗状态
    setTimeout(() => {
      this.currentBattle = null;
    }, 2000);
  }

  // 处理胜利奖励
  handleVictoryRewards() {
    const beast = this.currentBattle.beast;
    const player = this.currentBattle.player;

    // 获得经验值
    const xpGain = Math.floor(beast.stats.qi / 10) + beast.level * 5;
    this.addBattleLog(`获得${xpGain}点经验值！`);

    // 掉落物品
    if (beast.dropItems && beast.dropItems.length > 0) {
      const dropItem = beast.dropItems[Math.floor(Math.random() * beast.dropItems.length)];
      this.stateManager.addItemToBag(dropItem, 1);
      this.addBattleLog(`获得战利品：${dropItem}`);
    }

    // 恢复部分血量
    const healAmount = Math.floor(player.qi * 0.3);
    player.currentQi = Math.min(player.qi, player.currentQi + healAmount);
    this.addBattleLog(`恢复${healAmount}点血量`);

    // 更新玩家状态
    this.stateManager.updateAttribute('qi', player.currentQi);
  }

  // 处理失败惩罚
  handleDefeatPenalties() {
    const player = this.currentBattle.player;
    const state = this.stateManager.getState();

    // 损失血量和物品
    const qiLoss = Math.floor(player.qi * 0.2);
    player.currentQi = Math.max(1, player.currentQi - qiLoss);
    this.addBattleLog(`损失${qiLoss}点血量`);

    // 可能损失物品
    const bag = state.bag;
    const items = Object.keys(bag);
    if (items.length > 0 && Math.random() < 0.3) {
      const lostItem = items[Math.floor(Math.random() * items.length)];
      const lostCount = Math.min(bag[lostItem], Math.floor(Math.random() * 3) + 1);
      this.stateManager.removeItemFromBag(lostItem, lostCount);
      this.addBattleLog(`在战斗中丢失了${lostCount}个${lostItem}`);
    }

    // 添加经脉受损状态并减少气血上限
    if (!state.statusEffects) state.statusEffects = {};
    // 使用当前的最大气血作为基准，确保累积效果
    const currentMaxQi = state.maxQi;

    // 失败时减少10%的当前气血上限
    const qiReduction = Math.floor(currentMaxQi * 0.1);
    const newMaxQi = Math.max(1, currentMaxQi - qiReduction);

    state.statusEffects['经脉受损'] = {
      ...STATUS_EFFECTS['经脉受损'],
      appliedAt: Date.now()
    };

    this.addBattleLog('经脉受到重创，气血上限降低，需要服用续脉丹修复！');

    // 更新玩家状态
    this.stateManager.updateState({
      qi: Math.min(player.currentQi, newMaxQi), // 确保当前血量不超过新上限
      maxQi: newMaxQi,
      statusEffects: state.statusEffects
    });
  }

  // 检查受伤状态
  checkInjuryStatus() {
    const player = this.currentBattle.player;
    const state = this.stateManager.getState();
    const originalMaxQi = state.originalMaxQi || state.maxQi; // 使用状态中的maxQi，而不是player.qi

    // 如果当前血量低于原始最大血量的30%，添加重伤状态
    if (player.currentQi < originalMaxQi * 0.3) {
      if (!state.statusEffects) state.statusEffects = {};
      state.statusEffects['重伤'] = {
        ...STATUS_EFFECTS['重伤'],
        appliedAt: Date.now()
      };

      // 更新最大血量（减少）
      const qiReduction = Math.floor(originalMaxQi * 0.3); // 减少30%
      const newMaxQi = Math.max(1, originalMaxQi - qiReduction);
      state.maxQi = newMaxQi;

      this.addBattleLog('战斗中受伤严重，气血上限降低！');

      // 更新玩家状态
      this.stateManager.updateState({
        qi: Math.min(player.currentQi, newMaxQi), // 确保当前血量不超过新上限
        maxQi: newMaxQi,
        statusEffects: state.statusEffects,
        originalMaxQi: originalMaxQi // 确保保存原始最大气血
      });
    }
  }

  // 添加战斗日志
  addBattleLog(message) {
    if (this.currentBattle) {
      const newLog = {
        message: message,
        timestamp: Date.now()
      };
      this.currentBattle.log = [...this.currentBattle.log, newLog]; // 创建新数组以触发响应性

      // 限制日志长度
      if (this.currentBattle.log.length > 50) {
        this.currentBattle.log = this.currentBattle.log.slice(-50);
      }


    }
  }

  // 获取当前战斗状态
  getBattleState() {
    if (!this.currentBattle) return null;

    return {
      player: {
        currentQi: this.currentBattle.player.currentQi,
        maxQi: this.currentBattle.player.maxQi || this.currentBattle.player.qi,
        effects: this.currentBattle.player.effects
      },
      beast: {
        name: this.currentBattle.beast.name,
        description: this.currentBattle.beast.description,
        currentQi: this.currentBattle.beast.currentQi,
        maxQi: this.currentBattle.beast.stats.qi,
        effects: this.currentBattle.beast.effects,
        realm: this.currentBattle.beast.realm,
        level: this.currentBattle.beast.level
      },
      isPlayerTurn: this.currentBattle.isPlayerTurn,
      turn: this.currentBattle.turn,
      log: this.currentBattle.log.slice(-10), // 只返回最近10条日志
      battleResult: this.currentBattle.battleResult
    };
  }

  // 检查是否有正在进行的战斗
  isInBattle() {
    return this.currentBattle !== null;
  }

  // 强制结束战斗（用于异常情况）
  forceEndBattle() {
    if (this.currentBattle) {
      this.addBattleLog('战斗异常结束');
      this.currentBattle = null;
    }
  }
}

export default BattleManager;
